import { AppLayout, FormProtectionProvider, NvbLocaleProvider } from "@nvb/ui";
import type { ILocaleLink } from "@nvb/ui";
import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { usePageContext } from "vike-react/usePageContext";

import { refreshCountries } from "@/_data/countries";

import {
	DEFAULT_LANGUAGE,
	HREFLANG_BY_LOCALE,
	LANGUAGE_CONFIG,
	Locale,
	SUPPORTED_LANGUAGES,
} from "@/_mixins/locales/constants";
import { I18nProvider } from "@/_mixins/locales/I18nextProvider";
import { localizePath } from "@/_mixins/locales/routing";
import { getSiteFormProtectionConfig } from "@/_mixins/utils/formProtection";

import "@nvb/ui/dist/index.css";
import "./common.css";
// After the library CSS on purpose: restores the 1.0.5 rendering where @nvb/core 1.1 changed it.
import "@styles/core-1.1-compat.css";

// const COVER_PAGE_KEY = "nvb-cover-page-last-shown";
// const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export default function Wrapper({ children }: { children: ReactNode }) {
	const pageContext = usePageContext();
	const currentPath = pageContext.urlPathname;
	const protectionConfig = useMemo(() => getSiteFormProtectionConfig(), []);

	// Every form on the site — phone dial codes, masks, country selects — reads
	// the backoffice country guide instead of the list bundled with @nvb/ui.
	// Fetched once per page load rather than imported: the payload is ~200 KB of
	// JSON that no visitor who never opens a form needs in their bundle, and the
	// bundled list covers the gap until it lands.
	useEffect(() => {
		void refreshCountries();
	}, []);

	const locale = (pageContext.locale as Locale | undefined) ?? DEFAULT_LANGUAGE;
	// Locale-free path, so switching languages keeps the visitor on the same page.
	const logicalPath = pageContext.localizedPath ?? pageContext.urlLogical ?? "/";

	// Root-relative hrefs in the exact served form (trailing slash included, so
	// the link does not pass through a 301). The switcher being real links is
	// also how the alternate-language tree gets its internal inbound links.
	const localeLinks = useMemo<ILocaleLink[]>(
		() =>
			SUPPORTED_LANGUAGES.map((code) => ({
				code,
				tag: HREFLANG_BY_LOCALE[code],
				nativeName: LANGUAGE_CONFIG[code].nativeName,
				href: localizePath(logicalPath, code),
				active: code === locale,
			})),
		[logicalPath, locale],
	);

	// Internal hrefs inside @nvb/ui are authored locale-free (`/about/`), because
	// the library must not know how this app prefixes languages. Handing it the
	// prefixing rule keeps every header, footer, breadcrumb and CTA link inside
	// the language the visitor is reading — they used to drop back to English on
	// the first click out of an Armenian page.
	const localizeHref = useCallback((path: string) => localizePath(path, locale), [locale]);
	// const [showCover, setShowCover] = useState(false);

	// useEffect(() => {
	// 	// Check if we should show the cover page
	// 	const checkShouldShowCover = () => {
	// 		try {
	// 			const lastShown = localStorage.getItem(COVER_PAGE_KEY);
	// 			const now = Date.now();

	// 			if (!lastShown) {
	// 				// First time visitor - show cover and store timestamp
	// 				setShowCover(true);
	// 				localStorage.setItem(COVER_PAGE_KEY, now.toString());
	// 				return;
	// 			}

	// 			const lastShownTime = parseInt(lastShown, 10);
	// 			const timeSinceLastShown = now - lastShownTime;

	// 			if (timeSinceLastShown >= WEEK_IN_MS) {
	// 				// A week has passed - show cover and update timestamp
	// 				setShowCover(true);
	// 				localStorage.setItem(COVER_PAGE_KEY, now.toString());
	// 			}
	// 		} catch (error) {
	// 			// localStorage might not be available - skip cover page
	// 			console.warn("Could not access localStorage for cover page:", error);
	// 		}
	// 	};

	// 	checkShouldShowCover();
	// }, []);

	// useEffect(() => {
	// 	if (showCover) {
	// 		const timer = setTimeout(() => setShowCover(false), 2000); // Show for 2 seconds
	// 		return () => clearTimeout(timer);
	// 	}
	// }, [showCover]);

	// Bypass AppLayout for the self-contained landings — they ship their own nav
	// and footer and must not be wrapped in the shared chrome.
	//
	// The three `/events/` forms are here for the opposite reason: they ship no
	// chrome at all. A visitor arrives from an invitation to do one thing, and the
	// site header offered them every way out of it; the form redirects to the home
	// page once the registration is in, which is the only exit it needs.
	//
	// All three are listed by hand. `/events/pre-registration` does NOT match the
	// `/events/registration` prefix — the test is `startsWith`, and the path has
	// `pre-` in front — so a missing entry here is silent: the page renders, with
	// a header and a footer the other two deliberately do not have.
	//
	// Tested against `logicalPath`, not `currentPath`: the latter still carries
	// the locale prefix, so an `/hy/...` visitor would fall through to the
	// wrapped branch and get two headers.
	if (
		logicalPath.startsWith("/welcome") ||
		logicalPath.startsWith("/events/frontier-forum") ||
		logicalPath.startsWith("/events/registration") ||
		logicalPath.startsWith("/events/pre-registration") ||
		logicalPath.startsWith("/events/questions")
	) {
		return (
			<I18nProvider>
				<NvbLocaleProvider locale={locale} links={localeLinks} localizeHref={localizeHref}>
					<FormProtectionProvider config={protectionConfig}>{children}</FormProtectionProvider>
				</NvbLocaleProvider>
			</I18nProvider>
		);
	}

	// return showCover ? <CoverPage src="/assets/icons/logo.svg" /> :
	// I18nProvider was previously never mounted, so react-i18next fell back to the
	// default instance pinned to the default language and every localized page
	// rendered the same strings. It wraps everything so @nvb/ui components resolve
	// translations. NvbLocaleProvider feeds the language switcher real URLs.
	return (
		<I18nProvider>
			<NvbLocaleProvider locale={locale} links={localeLinks} localizeHref={localizeHref}>
				<FormProtectionProvider config={protectionConfig}>
					<AppLayout path={currentPath}>{children}</AppLayout>
				</FormProtectionProvider>
			</NvbLocaleProvider>
		</I18nProvider>
	);
}
